import { useState, useEffect, useRef, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

interface NextTopup {
  created_at: string;
  value: number;
  remark: string | null;
}

interface CrmCallInfo {
  renewal_status: 'UNDECIDED' | 'WILL_RENEW' | 'NOT_RENEWING' | 'RENEWED';
  linked_order_code: string | null;
  call_date: string;
}

interface StudentResult {
  student_code: string;
  student_name: string;
  nickname: string | null;
  status: 'depleted' | 'nearly_depleted' | 'predicted';
  event_date: string;
  after_hours_at_event: number;
  current_hours: number | null;
  next_topup: NextTopup | null;
  crm_info: CrmCallInfo | null;
  total_purchase: number | null;
  estimated_hours_depletion_date: string | null;
  estimated_8hours_date: string | null;
  weekly_scheduled_hours: number | null;
}

interface CrmSummary {
  total_with_crm: number;
  total_students: number;
  crm_coverage_percent: number;
  by_status: {
    renewed: number;
    will_renew: number;
    undecided: number;
    not_renewing: number;
  };
}

interface DepletionSummary {
  total_depleted: number;
  total_nearly_depleted: number;
  total_renewed: number;
  total_estimated_depleted: number;
  total_estimated_nearly_depleted: number;
  crm_summary: CrmSummary;
}

interface DepletionStatusResponse {
  students: StudentResult[];
  summary: DepletionSummary;
  date_range: {
    start: string;
    end: string;
  };
}

interface CachedData {
  data: DepletionStatusResponse | null;
  fetchedAt: number;
  startDate: string;
  endDate: string;
}

interface UseStudentDepletionStatusOptions {
  startDate: string;
  endDate: string;
}

const CACHE_DURATION_MS = 5 * 60 * 1000;

export function useStudentDepletionStatus(options: UseStudentDepletionStatusOptions) {
  const { startDate, endDate } = options;
  const [data, setData] = useState<DepletionStatusResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [lastFetchedAt, setLastFetchedAt] = useState<Date | null>(null);
  
  const cacheRef = useRef<CachedData | null>(null);
  const fetchIdRef = useRef(0);

  const shouldUseCachedData = useCallback(() => {
    if (!cacheRef.current) return false;
    if (cacheRef.current.startDate !== startDate || cacheRef.current.endDate !== endDate) return false;
    if (Date.now() - cacheRef.current.fetchedAt > CACHE_DURATION_MS) return false;
    return true;
  }, [startDate, endDate]);

  const fetchData = useCallback(async (forceRefresh = false) => {
    if (!forceRefresh && shouldUseCachedData()) {
      if (cacheRef.current?.data) {
        setData(cacheRef.current.data);
        setLastFetchedAt(new Date(cacheRef.current.fetchedAt));
      }
      return;
    }

    const currentFetchId = ++fetchIdRef.current;
    setIsLoading(true);

    try {
      const { data: responseData, error } = await supabase.functions.invoke('get-student-depletion-status', {
        body: { startDate, endDate, status: 'all' }
      });

      if (currentFetchId !== fetchIdRef.current) return;

      if (error) {
        console.error("Error fetching depletion status:", error);
        toast({ title: "เกิดข้อผิดพลาด", description: "ไม่สามารถดึงข้อมูลนักเรียนใกล้หมดคอร์สได้", variant: "destructive" });
        return;
      }

      const now = Date.now();
      const actualData = responseData?.data || responseData;

      cacheRef.current = { data: actualData, fetchedAt: now, startDate, endDate };
      setData(actualData);
      setLastFetchedAt(new Date(now));
    } catch (error) {
      console.error("Error in API call:", error);
      toast({ title: "เกิดข้อผิดพลาด", description: "เกิดข้อผิดพลาดในการเชื่อมต่อ", variant: "destructive" });
    } finally {
      if (currentFetchId === fetchIdRef.current) setIsLoading(false);
    }
  }, [startDate, endDate, shouldUseCachedData]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const refresh = useCallback(() => { fetchData(true); }, [fetchData]);

  return { data, isLoading, lastFetchedAt, refresh };
}